import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FiUpload, FiX, FiAlertCircle } from 'react-icons/fi';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { isImageFile, processCmrFileForUpload } from '../../../utils/cmrFileProcessing';

const CMR_ACCEPT = '.pdf,.jpg,.jpeg,.png';

export { CMR_MAX_BYTES } from '../../../utils/cmrFileProcessing';

export default function CmrFileDropzone({ file, onFileChange, error, label = 'CMR document' }) {
  const fileInputRef = useRef(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!file || !isImageFile(file)) {
      setImagePreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const applyFile = async (f) => {
    if (!f) return;
    setProcessing(true);
    try {
      const result = await processCmrFileForUpload(f);
      if (!result.ok) {
        toast.error(result.error);
        return;
      }
      if (result.wasCompressed) {
        toast.success('Image compressed to under 2 MB.');
      }
      onFileChange(result.file);
    } finally {
      setProcessing(false);
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) applyFile(f);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!processing) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (processing) return;
    const f = e.dataTransfer.files?.[0];
    if (f) applyFile(f);
  };

  const dropzoneClass = processing
    ? 'border-gray-200 bg-gray-50 cursor-wait opacity-80'
    : isDragging
      ? 'border-green-400 bg-green-50/50'
      : file
        ? 'border-green-400 bg-green-50/50'
        : 'border-gray-200 bg-gray-50 hover:border-green-400 hover:bg-green-50/30';

  return (
    <div>
      {label && <p className="text-sm font-medium text-gray-700 mb-1">{label}</p>}
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1 mb-1">
          <FiAlertCircle size={12} />
          {error}
        </p>
      )}
      <div
        role="button"
        tabIndex={processing ? -1 : 0}
        onClick={() => !processing && fileInputRef.current?.click()}
        onKeyDown={(e) => e.key === 'Enter' && !processing && fileInputRef.current?.click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-5 flex flex-col items-center gap-2 transition-all
          ${processing ? 'pointer-events-none' : 'cursor-pointer'}
          ${dropzoneClass}`}
      >
        {processing ? (
          <>
            <svg className="animate-spin h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" aria-hidden>
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
            </svg>
            <p className="text-sm text-gray-600 font-medium">Compressing image…</p>
          </>
        ) : file ? (
          <div className="flex flex-col items-center gap-3 w-full">
            {imagePreviewUrl ? (
              <div className="w-full rounded-lg overflow-hidden border border-green-200/80 bg-gray-50 max-h-52 flex items-center justify-center">
                <img
                  src={imagePreviewUrl}
                  alt={`Preview: ${file.name}`}
                  className="max-h-52 w-full object-contain"
                />
              </div>
            ) : (
              <div
                className="w-20 h-20 rounded-xl bg-green-100 border border-green-200/70 flex items-center justify-center"
                aria-hidden
              >
                <HiOutlineDocumentText className="w-10 h-10 text-green-700" />
              </div>
            )}
            <div className="text-center min-w-0 w-full">
              <p className="text-sm text-gray-700 font-medium break-all">{file.name}</p>
              <p className="text-xs text-gray-400 mt-0.5">{(file.size / 1024).toFixed(1)} KB</p>
            </div>
          </div>
        ) : (
          <>
            <FiUpload size={22} className="text-gray-400" />
            <p className="text-sm text-gray-600 font-medium">Drag & drop CMR here</p>
            <p className="text-sm text-gray-500">or click to browse</p>
            <p className="text-xs text-gray-400 text-center">
              PDF max 2 MB · Images up to 6 MB (auto-compressed to 2 MB)
            </p>
          </>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={CMR_ACCEPT}
        disabled={processing}
        onChange={handleFileChange}
      />
      {file && !processing && (
        <button
          type="button"
          onClick={() => onFileChange(null)}
          className="mt-2 text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <FiX size={12} /> Remove file
        </button>
      )}
    </div>
  );
}
